import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Coins, Plus, Trash2, Trophy, Download, Search, RefreshCw } from "lucide-react";

const empty = { sku: "", product_title: "", amount: 0, store_id: "" };

function Leaderboard({ stores }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  useEffect(() => {
    setLoading(true);
    api.get("/agent/leaderboard").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);

  const csvUrl = (agent_id) => {
    const p = new URLSearchParams();
    if (month) p.set("month", month);
    if (agent_id) p.set("agent_id", agent_id);
    return `${process.env.REACT_APP_BACKEND_URL}/api/incentives/export.csv?${p.toString()}`;
  };

  const storeName = (id) => stores.find((s) => s.id === id)?.name || "—";

  return (
    <div className="card-shell p-5">
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="data-label flex items-center gap-1"><Trophy className="w-3 h-3" /> Payout tracker</div>
          <h3 className="font-heading font-bold text-lg mt-0.5">Agent leaderboard</h3>
          <p className="text-xs text-[#71717A]">Ranked by total incentive earned across all months.</p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="data-label mb-1 block">Payout month</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-[180px]"
              data-testid="incentive-month-picker"
            />
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => window.open(csvUrl(), "_blank")}
            data-testid="export-incentives-csv"
          >
            <Download className="w-4 h-4 mr-1" /> Export payout CSV
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Agent</th>
              <th>Salesperson ID</th>
              <th>Store</th>
              <th className="text-right">Registrations</th>
              <th className="text-right">Total incentive</th>
              <th></th>
            </tr>
          </thead>
          <tbody data-testid="leaderboard-tbody">
            {loading ? (
              <tr><td colSpan={7} className="p-6 text-center text-[#71717A]">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-center text-[#71717A]">No sales agents yet.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.id} data-testid={`leaderboard-row-${r.id}`}>
                <td className="font-mono text-xs">
                  {i === 0 && <span className="text-amber-500">🥇</span>}
                  {i === 1 && <span className="text-slate-400">🥈</span>}
                  {i === 2 && <span className="text-orange-600">🥉</span>}
                  {i > 2 && `${i + 1}`}
                </td>
                <td>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-[#71717A]">{r.email}</div>
                </td>
                <td className="font-mono text-xs">{r.salesperson_id || "—"}</td>
                <td className="text-sm">{storeName(r.store_id)}</td>
                <td className="text-right font-mono">{r.total_registrations}</td>
                <td className="text-right font-mono font-medium text-[#059669]">₹{r.total_incentive.toLocaleString("en-IN")}</td>
                <td className="text-right">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => window.open(csvUrl(r.id), "_blank")}
                    data-testid={`export-agent-${r.id}`}
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function IncentivesPage() {
  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/product_incentives");
    setRows(r.data);
  }, []);
  useEffect(() => {
    load();
    api.get("/stores").then((r) => setStores(r.data));
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sku.trim()) return toast.error("SKU is required");
    if (Number(form.amount) < 0) return toast.error("Amount must be ≥ 0");
    try {
      await api.post("/product_incentives", {
        sku: form.sku.trim(),
        product_title: form.product_title || "",
        amount: Number(form.amount),
        store_id: form.store_id || null,
      });
      toast.success("Incentive saved");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const del = async (sku) => {
    if (!window.confirm(`Remove incentive for SKU ${sku}?`)) return;
    try {
      await api.delete(`/product_incentives/${encodeURIComponent(sku)}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const toggleRow = (sku) => setSelected((prev) => {
    const s = new Set(prev);
    if (s.has(sku)) s.delete(sku); else s.add(sku);
    return s;
  });
  const toggleAll = (list) => setSelected((prev) => {
    if (prev.size === list.length) return new Set();
    return new Set(list.map((r) => r.sku));
  });
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const arr = Array.from(selected);
    if (!arr.length) return;
    if (!window.confirm(`Delete ${arr.length} incentive rule${arr.length === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/product_incentives/bulk_delete", { skus: arr });
      toast.success(`Deleted ${r.data.deleted} rule${r.data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const [editing, setEditing] = useState(null); // incentive doc being edited

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing.sku.trim()) return toast.error("SKU is required");
    setBusy(true);
    try {
      await api.patch(`/product_incentives/by_id/${editing.id}`, {
        sku: editing.sku.trim(),
        product_title: editing.product_title || "",
        amount: Number(editing.amount),
        store_id: editing.store_id || null,
      });
      toast.success("Incentive updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const recompute = async () => {
    if (!window.confirm("Re-run the matcher on all past product registrations that still show ₹0 or unmatched? Safe to re-run.")) return;
    setBusy(true);
    try {
      const r = await api.post("/records/recompute_incentives");
      toast.success(`Scanned ${r.data.scanned} · updated ${r.data.updated} · ₹>0 on ${r.data.with_incentive}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Recompute failed");
    } finally {
      setBusy(false);
    }
  };

  const filtered = rows.filter((r) => {
    const s = q.toLowerCase();
    return !s || r.sku.toLowerCase().includes(s) || (r.product_title || "").toLowerCase().includes(s);
  });

  const storeName = (id) => stores.find((s) => s.id === id)?.name || "All stores";

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="data-label mb-1">Sales incentive SOP</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">Incentives</h1>
          <p className="text-sm text-[#71717A] mt-1">
            Map each product SKU to the incentive amount an agent earns per registration. Empty store = applies to all stores.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={recompute} disabled={busy} data-testid="recompute-incentives-button" title="Retry the matcher on all past ₹0 registrations">
            <RefreshCw className="w-4 h-4 mr-1" /> Recompute ₹0 records
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="add-incentive-button">
                <Plus className="w-4 h-4 mr-1" /> Add / update incentive
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Product incentive</DialogTitle>
              <DialogDescription>The SKU should match the Unique Product ID entered on the registration form. Re-submitting the same SKU updates the amount.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="data-label mb-1.5 block">Product SKU / Unique Product ID</Label>
                <Input required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="incentive-sku-input" placeholder="e.g. HEM-WC-101" />
              </div>
              <div>
                <Label className="data-label mb-1.5 block">Product title (optional)</Label>
                <Input value={form.product_title} onChange={(e) => setForm({ ...form, product_title: e.target.value })} data-testid="incentive-title-input" />
              </div>
              <div>
                <Label className="data-label mb-1.5 block">Incentive amount (INR)</Label>
                <Input type="number" min="0" step="1" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="incentive-amount-input" />
              </div>
              <div>
                <Label className="data-label mb-1.5 block">Store (optional)</Label>
                <Select value={form.store_id || "__all"} onValueChange={(v) => setForm({ ...form, store_id: v === "__all" ? "" : v })}>
                  <SelectTrigger data-testid="incentive-store-select"><SelectValue placeholder="All stores" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All stores</SelectItem>
                    {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="bg-[#4F46E5] hover:bg-[#4338CA] text-white w-full" data-testid="incentive-submit">
                Save incentive
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="card-shell p-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <Input
            className="pl-9"
            placeholder="Search by SKU or product name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="incentive-search-input"
          />
        </div>
      </div>

      {selected.size > 0 && (
        <div className="card-shell p-3 flex items-center justify-between gap-3 bg-[#FEF3C7] border-[#FCD34D]" data-testid="incentive-bulk-bar">
          <div className="text-sm">
            <span className="font-medium">{selected.size}</span> rule{selected.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={clearSelection} data-testid="incentive-clear-selection">Clear</Button>
            <Button size="sm" variant="outline" className="text-[#DC2626] hover:bg-[#FEE2E2]" onClick={bulkDelete} disabled={busy} data-testid="incentive-bulk-delete">
              <Trash2 className="w-3 h-3 mr-1" /> Delete selected
            </Button>
          </div>
        </div>
      )}

      <div className="card-shell overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <Checkbox
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onCheckedChange={() => toggleAll(filtered)}
                    data-testid="incentive-select-all"
                  />
                </th>
                <th>SKU</th><th>Product title</th><th>Store scope</th><th className="text-right">Incentive (₹)</th><th></th>
              </tr>
            </thead>
            <tbody data-testid="incentives-tbody">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-[#71717A] text-sm">
                    <Coins className="w-8 h-8 mx-auto mb-2 text-[#A1A1AA]" />
                    {rows.length === 0 ? "No incentives configured yet." : "No matches."}
                  </td>
                </tr>
              ) : filtered.map((r) => (
                <tr key={r.sku} data-testid={`incentive-row-${r.sku}`}>
                  <td>
                    <Checkbox
                      checked={selected.has(r.sku)}
                      onCheckedChange={() => toggleRow(r.sku)}
                      data-testid={`incentive-check-${r.sku}`}
                    />
                  </td>
                  <td className="font-mono">{r.sku}</td>
                  <td className="text-sm">{r.product_title || "—"}</td>
                  <td className="text-xs text-[#52525B]">{storeName(r.store_id)}</td>
                  <td className="text-right font-mono font-medium">₹{Number(r.amount).toLocaleString("en-IN")}</td>
                  <td className="text-right flex justify-end gap-1">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...r })} data-testid={`edit-incentive-${r.sku}`}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => del(r.sku)} data-testid={`delete-incentive-${r.sku}`}>
                      <Trash2 className="w-4 h-4 text-[#DC2626]" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit incentive</DialogTitle>
            <DialogDescription>
              Update the SKU, product title, amount, or store scope. SKU variants like
              <span className="font-mono"> E-Winn 101 A</span>, <span className="font-mono">E-Winn 101A</span> and
              <span className="font-mono"> EWinn 101 A</span> are treated as the same SKU automatically.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div>
                <Label className="data-label mb-1.5 block">Product SKU</Label>
                <Input required value={editing.sku} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} data-testid="edit-incentive-sku" />
              </div>
              <div>
                <Label className="data-label mb-1.5 block">Product title</Label>
                <Input value={editing.product_title || ""} onChange={(e) => setEditing({ ...editing, product_title: e.target.value })} data-testid="edit-incentive-title" />
              </div>
              <div>
                <Label className="data-label mb-1.5 block">Incentive amount (INR)</Label>
                <Input type="number" min="0" step="1" required value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} data-testid="edit-incentive-amount" />
              </div>
              <div>
                <Label className="data-label mb-1.5 block">Store (optional)</Label>
                <Select value={editing.store_id || "__all"} onValueChange={(v) => setEditing({ ...editing, store_id: v === "__all" ? "" : v })}>
                  <SelectTrigger data-testid="edit-incentive-store"><SelectValue placeholder="All stores" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All stores</SelectItem>
                    {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={busy} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white w-full" data-testid="edit-incentive-submit">
                Save changes
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Leaderboard stores={stores} />
    </div>
  );
}
